import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import styles from './TagsModal.module.scss';

/**
 * 🏷️ TagsModal - Modal para gerenciar tags dos cards
 * 
 * Features:
 * - Listar tags disponíveis
 * - Criar nova tag (nome + cor)
 * - Associar/desassociar tags ao card
 * - Paleta de cores predefinida
 */

const TagsModal = ({ 
  show, 
  onHide, 
  cardId, 
  cardTags = [], 
  availableTags = [],
  onCreateTag,
  onAddTagToCard,
  onRemoveTagFromCard,
  colors = []
}) => {
  const [isCreating, setIsCreating] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [selectedColor, setSelectedColor] = useState('#6366F1');
  const [loading, setLoading] = useState(false);
  
  // Estado local para as tags do card (atualização otimista)
  const [localCardTags, setLocalCardTags] = useState([]);

  // Garantir que availableTags e colors sejam arrays
  const safeAvailableTags = Array.isArray(availableTags) ? availableTags : [];
  const safeColors = Array.isArray(colors) ? colors : [];
  const safeCardTags = Array.isArray(cardTags) ? cardTags : [];
  
  // Sincronizar localCardTags com cardTags quando o modal abre ou cardTags muda
  useEffect(() => {
    if (show) {
      setLocalCardTags(safeCardTags);
    }
  }, [show, JSON.stringify(safeCardTags)]);

  // Reset form when modal closes
  useEffect(() => {
    if (!show) {
      setIsCreating(false);
      setNewTagName('');
      setSelectedColor('#6366F1');
    }
  }, [show]);


  // Verificar se uma tag está associada ao card (usando estado local)
  const isTagAssociated = (tagId) => {
    return localCardTags.some(tag => tag.id === tagId);
  };

  // Criar nova tag
  const handleCreateTag = async () => {
    if (!newTagName.trim()) {
      alert('Digite um nome para a tag');
      return;
    }

    if (newTagName.length > 50) {
      alert('O nome da tag deve ter no máximo 50 caracteres');
      return;
    }

    setLoading(true);
    try {
      await onCreateTag(newTagName.trim(), selectedColor);
      setNewTagName('');
      setSelectedColor('#6366F1');
      setIsCreating(false);
    } catch (error) {
      console.error('Erro ao criar tag:', error);
      alert(error.message || 'Erro ao criar tag');
    } finally {
      setLoading(false);
    }
  };

  // Associar/desassociar tag (com atualização otimista)
  const handleToggleTag = async (tag) => {
    const isCurrentlyAssociated = isTagAssociated(tag.id);
    
    // Atualização otimista - atualiza UI imediatamente
    if (isCurrentlyAssociated) {
      // Remover da lista local
      setLocalCardTags(prev => prev.filter(t => t.id !== tag.id));
    } else {
      // Adicionar à lista local
      setLocalCardTags(prev => [...prev, { id: tag.id, name: tag.name, color: tag.color }]);
    }
    
    setLoading(true);
    try {
      if (isCurrentlyAssociated) {
        await onRemoveTagFromCard(cardId, tag.id);
      } else {
        await onAddTagToCard(cardId, tag.id);
      }
    } catch (error) {
      console.error('Erro ao associar/desassociar tag:', error);
      alert(error.message || 'Erro ao gerenciar tag');
      
      // Reverter em caso de erro
      if (isCurrentlyAssociated) {
        setLocalCardTags(prev => [...prev, { id: tag.id, name: tag.name, color: tag.color }]);
      } else {
        setLocalCardTags(prev => prev.filter(t => t.id !== tag.id));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide}
      centered
      backdrop="static"
      backdropClassName="modal-backdrop-lightbox"
      className={styles.tagsModal}
    >
      <Modal.Header closeButton className={styles.modalHeader}>
        <Modal.Title className={styles.modalTitle}>
          <Icon icon="mdi:tag-multiple" className={styles.titleIcon} />
          Gerenciar Tags
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className={styles.modalBody}>
        {/* Criar Nova Tag */}
        <div className={styles.createSection}>
          {!isCreating ? (
            <button 
              type="button"
              className={styles.createButton}
              onClick={() => setIsCreating(true)}
              disabled={loading}
            >
              <Icon icon="mdi:plus-circle" />
              Criar nova tag
            </button>
          ) : (
            <div className={styles.createForm}>
              {/* Input Nome */}
              <input
                type="text"
                className={styles.tagInput}
                placeholder="Nome da tag (máx. 50 caracteres)"
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                maxLength={50}
                disabled={loading}
              />

              {/* Contador de caracteres */}
              <div className={styles.charCounter}>
                {newTagName.length}/50
              </div>

              {/* Paleta de Cores */}
              <div className={styles.colorPalette}>
                {safeColors.map((color) => (
                  <button
                    type="button"
                    key={color.value}
                    className={`${styles.colorButton} ${selectedColor === color.value ? styles.selected : ''}`}
                    style={{ backgroundColor: color.value }}
                    onClick={() => setSelectedColor(color.value)}
                    title={color.name}
                    disabled={loading}
                  />
                ))}
              </div>

              {/* Preview */}
              {newTagName && (
                <div className={styles.preview}>
                  <span className={styles.previewLabel}>Preview:</span>
                  <span 
                    className={styles.tagPreview}
                    style={{ backgroundColor: selectedColor }}
                  >
                    {newTagName}
                  </span>
                </div>
              )}

              {/* Ações */}
              <div className={styles.formActions}>
                <button 
                  type="button"
                  className={styles.cancelButton}
                  onClick={() => {
                    setIsCreating(false);
                    setNewTagName('');
                    setSelectedColor('#6366F1');
                  }}
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button 
                  type="button"
                  className={styles.saveButton}
                  onClick={handleCreateTag}
                  disabled={loading || !newTagName.trim()}
                >
                  {loading ? (
                    <>
                      <Icon icon="mdi:loading" className={styles.spin} />
                      Criando...
                    </>
                  ) : (
                    <>
                      <Icon icon="mdi:check" />
                      Criar Tag
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Divisor */}
        <div className={styles.divider} />

        {/* Lista de Tags Disponíveis */}
        <div className={styles.tagsSection}>
          <span 
            className={styles.sectionTitle}
            style={{ 
              fontSize: '11px', 
              display: 'block',
              fontWeight: '600'
            }}
          >
            Tags Disponíveis ({safeAvailableTags.length})
          </span>

          {safeAvailableTags.length === 0 ? (
            <div className={styles.emptyState}>
              <Icon icon="mdi:tag-off-outline" className={styles.emptyIcon} />
              <p className={styles.emptyText}>
                Nenhuma tag criada ainda.
                <br />
                Crie sua primeira tag acima!
              </p>
            </div>
          ) : (
            <div className={styles.tagsList}>
              {safeAvailableTags.map((tag) => {
                const associated = isTagAssociated(tag.id);
                return (
                  <button
                    type="button"
                    key={tag.id}
                    className={`${styles.tagItem} ${associated ? styles.associated : ''}`}
                    onClick={() => handleToggleTag(tag)}
                    disabled={loading}
                  >
                    <span 
                      className={styles.tagDot}
                      style={{ backgroundColor: tag.color }}
                    />
                    <span className={styles.tagName}>{tag.name}</span>
                    {tag.usageCount > 0 && (
                      <span className={styles.usageCount}>
                        {tag.usageCount}
                      </span>
                    )}
                    <Icon 
                      icon={associated ? 'mdi:check-circle' : 'mdi:plus-circle-outline'} 
                      className={styles.tagAction}
                    />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer className={styles.modalFooter}>
        <button type="button" className={styles.closeButton} onClick={onHide}>
          Fechar
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default TagsModal;
